/* @flow */
import * as React from 'react';
import { observer, inject } from 'mobx-react';
import { reaction, computed } from 'mobx';
import { translate } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { TranslatorProps } from 'react-i18next';

import DashboardStore from 'stores/DashboardStore';
import { updateEditOwnedAsset, updateEditRentedAsset } from 'permissions/asset';
import navigation from 'services/NavigationService';
import { ComponentWithInjectedProps } from 'utils/injectedProps';
import { isIE } from 'utils/browserDetection';
import type { Status } from 'utils/statuses';
import { getCode } from 'utils/statuses';

import ChartTable from '../../components/ChartTable/ChartTable';
import NivoPieChart from '../../components/NivoPieChart';
import type { CommonWidgetProps } from '../../commonTypes';

const STATIC_DATA = [
  {
    Faulty: 36,
    Storage: 10,
    Delivered: 15,
    Inbound: 30,
    Sold: 10,
  },
];

const STATUS_EXCEPTIONS: Status[] = ['faulty', 'in_use', 'not_in_use', 'storage'];

const mapStatusToTypeFilterValues = (status: ?Status) => {
  switch (status) {
    case 'lost':
    case 'faulty':
    case 'in_use':
    case 'not_in_use':
    case 'storage':
    case 'disposed':
    case 'external_repair':
    case 'internal_repair':
    case 'beyond_economic_repair':
    case 'internal_calibration':
    case 'external_calibration':
      return ['Owned'];

    case 'in_repair':
    case 'inbound':
    case 'on_lease':
    case 'on_rent':
    case 'outside_repair':
    case 'returned':
      return ['Rented Open'];

    case 'sold':
      return ['Purchased'];

    default:
      return ['Rented Open', 'Purchased'];
  }
};

export type AssetStatusProps = CommonWidgetProps;

type InjectedProps = {
  dashboardStore: DashboardStore,
} & TranslatorProps;

@translate()
@inject('dashboardStore')
@observer
export default class AssetStatus extends ComponentWithInjectedProps<
  AssetStatusProps,
  {},
  InjectedProps
> {
  static LOAD_THUMBNAIL = false;

  @computed get shouldLoadData(): boolean {
    const { size } = this.props;

    return size !== 'thumbnail' || AssetStatus.LOAD_THUMBNAIL;
  }

  @computed get widgetData(): any[] | null {
    const { dashboardStore } = this.props;

    if (!this.shouldLoadData) {
      return STATIC_DATA;
    }

    return dashboardStore.statuses ? dashboardStore.statuses['assets-status'] : null;
  }

  autoUpdateDisposer: Function;

  componentDidMount() {
    const { dashboardStore } = this.props;

    this.loadWidgetData(dashboardStore.loadedTradingAccount);

    this.autoUpdateDisposer = reaction(
      () => dashboardStore.loadedTradingAccount,
      ta => {
        this.loadWidgetData(ta);
      }
    );
  }

  loadWidgetData = (tradingAccount: string) => {
    const { dashboardStore } = this.props;

    if (this.shouldLoadData) {
      dashboardStore.loadAssetStatus(tradingAccount);
    }
  };

  componentWillUnmount() {
    this.autoUpdateDisposer();
  }

  render() {
    const { dashboardStore, t, widgetItem, size, isFullScreen } = this.props;
    const accountId = dashboardStore.loadedTradingAccount;

    const color = [
      'hsl(86, 23%, 70%)',
      'hsl(85, 43%, 42%)',
      'hsl(55, 66%, 76%)',
      'hsl(11, 89%, 60%)',
      'hsl(187, 84%, 56%)',
      'hsl(198, 93%, 38%)',
      'hsl(206, 47%, 79%)',
      'hsl(197, 8%, 67%)',
      'hsl(197, 16%, 29%)',
      'hsl(0, 0%, 84%)',
      'hsl(201, 14%, 19%)',
    ];

    function firstToUpperCase(str) {
      return str.toUpperCase();
    }

    const assetData = this.widgetData
      ? Object.keys(this.widgetData[0]).map((key, index) => {
          const formattedKey = key
            .split('_')
            .join(' ')
            .replace(/(?!of)\b[a-z]/g, firstToUpperCase);

          return {
            id: formattedKey,
            // TODO: The backend needs to send proper keys back, and not readable content
            label: formattedKey,
            value: this.widgetData[0][key],
            color: color[index],
          };
        })
      : null;

    return (
      <NivoPieChart
        widgetInfos={[{ title: t('general:assets.labels.status'), variant: 'default' }]}
        widgetItem={widgetItem}
        size={size}
        isFullScreen={isFullScreen}
        isIE={isIE}
        isLoading={this.shouldLoadData && dashboardStore.loadingStatuses}
        data={assetData}
        permissions={[...updateEditOwnedAsset, ...updateEditRentedAsset]}
        button
        buttonLink={navigation.formulateReportViewUri('/assets', {
          page: 1,
          filters: [...(accountId ? [{ key: 'account', values: [accountId] }] : [])],
        })}
        buttonText={t('general:actions.edit_asset_status')}
        totalKey="value"
        totalTitle={count => t('general:asset', { count })}
        renderLegend={(statusData: any) => (
          <Link
            key={statusData.id}
            to={navigation.formulateReportViewUri('/assets', {
              page: 1,
              filters: [
                ...(accountId ? [{ key: 'account', values: [accountId] }] : []),
                { key: 'asset_type', values: mapStatusToTypeFilterValues(getCode(statusData.id)) },
                { key: 'asset_status', values: [statusData.id] },
              ],
            })}
          >
            <ChartTable
              title={statusData.label}
              number={statusData.value}
              color={statusData.color}
            />
          </Link>
        )}
        onClick={e => {
          navigation.navigateToReportView('/assets', {
            page: 1,
            filters: [
              ...(accountId ? [{ key: 'account', values: [accountId] }] : []),
              ...(STATUS_EXCEPTIONS.includes(getCode(e.id))
                ? [{ key: 'asset_type', values: ['Owned'] }]
                : [{ key: 'asset_type', values: ['Rented Open', 'Purchased'] }]),
              { key: 'asset_status', values: [e.id] },
            ],
          });
        }}
      />
    );
  }
}
